<template>
  <v-select
    :value="value"
    :options="$tOptions(displayOptions)"
    :filterable="false"
    :placeholder="translatedPlaceholder"
    :loading="initialLoading"
    :class="{ 'vs-sm': size === 'sm' }"
    label="label"
    @search="searchAgents"
    @input="handleInput"
    @open="onDropdownOpen"
  >
    <template #option="option">
      <span class="agent-username">{{ option.username }}</span>
      <span
        v-if="option.prefix"
        class="agent-prefix"
      >({{ option.prefix }})</span>
    </template>
    <template #selected-option="option">
      <span class="agent-username">{{ option.username }}</span>
      <span
        v-if="option.prefix"
        class="agent-prefix"
      >({{ option.prefix }})</span>
    </template>
    <template #list-footer>
      <li
        v-show="hasMoreAgents"
        ref="loadMoreTrigger"
        class="text-center py-2"
        style="cursor: pointer;"
        @click="loadMoreAgents"
      >
        <span v-if="loadingMore">{{ $t('กำลังโหลด...') }}</span>
        <span v-else>{{ $t('โหลดเพิ่มเติม') }}</span>
      </li>
    </template>
    <template #no-options>
      <span v-if="initialLoading">{{ $t('กำลังโหลดข้อมูล...') }}</span>
      <span v-else-if="currentSearchQuery">{{ $t('ไม่พบตัวแทน {query}', { query: currentSearchQuery }) }}</span>
      <span v-else>{{ $t('ไม่มีข้อมูลตัวแทน') }}</span>
    </template>
  </v-select>
</template>

<script>
import vSelect from 'vue-select'
import agentService from '@/service/agentService'

export default {
  name: 'AgentSelector',
  components: {
    vSelect,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    placeholder: {
      type: String,
      default: 'พิมพ์ username เพื่อค้นหา...',
    },
    size: {
      type: String,
      default: null,
    },
    // false = ไม่ auto-emit agent คนแรกตอน mount (ใช้กับ filter form ที่ค่าเริ่มต้นควรเป็นว่าง)
    autoSelect: {
      type: Boolean,
      default: true,
    },
    lazy: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      agentOptions: [],
      currentPage: 1,
      hasMoreAgents: true,
      loadingMore: false,
      initialLoading: true,
      currentSearchQuery: '',
      observer: null,
      requestId: 0,
      skipNextClear: false,
      hasLoadedDefault: false,
    }
  },
  computed: {
    translatedPlaceholder() {
      return this.$te(this.placeholder) ? this.$t(this.placeholder) : this.placeholder
    },
    displayOptions() {
      // Ensure selected value is always in the list
      if (this.value && this.value.code) {
        const isInList = this.agentOptions.some(opt => opt.code === this.value.code)
        if (!isInList) {
          // Selected value not in current list - add it to the top
          return [this.value, ...this.agentOptions]
        }
      }
      return this.agentOptions
    },
  },
  async mounted() {
    if (this.lazy) {
      this.initialLoading = false
      return
    }

    await this.loadDefaultAgent(this.autoSelect)
  },
  beforeDestroy() {
    this.cleanup()
  },
  methods: {
    cleanup() {
      if (this.observer) {
        this.observer.disconnect()
      }
    },

    mapAgentToOption(agent) {
      return {
        label: agent.prefix ? `${agent.username} (${agent.prefix})` : agent.username,
        code: agent.username,
        username: agent.username,
        prefix: agent.prefix,
      }
    },

    async loadDefaultAgent(shouldAutoSelect = false) {
      this.initialLoading = true
      try {
        const res = await agentService.getAuthen('downlines?page=1&limit=50')
        if (res.status === 200 && res.data?.data) {
          if (res.data.data.length > 0) {
            const firstAgent = this.mapAgentToOption(res.data.data[0])
            this.agentOptions = res.data.data.map(this.mapAgentToOption)
            this.currentPage = 1
            this.hasMoreAgents = this.agentOptions.length < res.data.total

            // Only emit default agent on initial load, not when clearing search
            if (shouldAutoSelect) {
              // If parent already has a value (e.g. restored from localStorage), keep it
              if (this.value && this.value.code) {
                this.$emit('change', this.value)
              } else {
                // Check if first agent is current user
                const currentUsername = this.$store.state.app.userData?.username
                const isCurrentUser = currentUsername && firstAgent.code === currentUsername

                if (isCurrentUser) {
                  this.$emit('input', null)
                  this.$emit('change', null)
                } else {
                  this.$emit('input', firstAgent)
                  this.$emit('change', firstAgent)
                }
              }
            }
          } else {
            // No agents available
            this.agentOptions = []
            this.hasMoreAgents = false
          }
        }
      } catch (error) {
        this.agentOptions = []
        this.hasMoreAgents = false
        this.$emit('error', error)
      } finally {
        this.hasLoadedDefault = true
        this.initialLoading = false
      }
    },
    async searchAgents(query, loading) {
      const newQuery = query || ''

      if (!newQuery || newQuery.length < 2) {
        // Skip if this clear is from selecting an item
        if (this.skipNextClear) {
          this.skipNextClear = false
          return
        }

        // Clear search - reload default list
        this.currentSearchQuery = ''
        await this.loadDefaultAgent(false)
        return
      }

      this.currentSearchQuery = newQuery

      this.requestId += 1
      const currentRequestId = this.requestId

      loading(true)
      try {
        const res = await agentService.getAuthen(`downlines?search=${newQuery}&page=1&limit=50`)

        // Check if this is still the latest request
        if (currentRequestId !== this.requestId) {
          return
        }

        if (res.status === 200 && res.data?.data) {
          this.agentOptions = res.data.data.map(this.mapAgentToOption)
          this.currentPage = 1
          this.hasMoreAgents = this.agentOptions.length < res.data.total
        }
      } catch (error) {
        if (currentRequestId === this.requestId) {
          this.agentOptions = []
          this.hasMoreAgents = false
        }
      } finally {
        if (currentRequestId === this.requestId) {
          loading(false)
        }
      }
    },
    async loadMoreAgents() {
      if (this.loadingMore || !this.hasMoreAgents) return

      this.loadingMore = true
      const nextPage = this.currentPage + 1

      // Save scroll position
      const dropdownList = document.querySelector('.vs__dropdown-menu')
      const scrollTop = dropdownList ? dropdownList.scrollTop : 0

      try {
        const searchParam = this.currentSearchQuery ? `&search=${this.currentSearchQuery}` : ''
        const res = await agentService.getAuthen(`downlines?page=${nextPage}&limit=50${searchParam}`)

        if (res.status === 200 && res.data?.data) {
          const newAgents = res.data.data.map(this.mapAgentToOption)
          this.agentOptions = [...this.agentOptions, ...newAgents]
          this.currentPage = nextPage
          this.hasMoreAgents = this.agentOptions.length < res.data.total

          // Restore scroll position
          this.$nextTick(() => {
            if (dropdownList) {
              dropdownList.scrollTop = scrollTop
            }
          })
        }
      } catch (error) {
        // Error handling without console
      } finally {
        this.loadingMore = false
      }
    },
    async onDropdownOpen() {
      if (this.lazy && !this.hasLoadedDefault) {
        await this.loadDefaultAgent(false)
      }

      this.$nextTick(() => {
        this.setupIntersectionObserver()
      })
    },
    setupIntersectionObserver() {
      if (!this.$refs.loadMoreTrigger) return

      // Disconnect old observer if exists
      if (this.observer) {
        this.observer.disconnect()
      }

      this.observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && this.hasMoreAgents && !this.loadingMore) {
          this.loadMoreAgents()
        }
      }, {
        threshold: 0.5,
      })

      this.observer.observe(this.$refs.loadMoreTrigger)
    },
    async handleInput(value) {
      // When selecting an item, set flag to skip the auto-clear reload
      if (value !== null) {
        this.skipNextClear = true
      } else {
        // When clearing (x button), reload default agent list
        this.currentSearchQuery = ''
        await this.loadDefaultAgent(false)
      }

      this.$emit('input', value)
      this.$emit('change', value)
    },
  },
}
</script>

<style scoped>
.agent-username {
  font-weight: normal;
}

.agent-prefix {
  font-size: 0.85em;
  opacity: 0.6;
  margin-left: 4px;
}

/* Vuexy sm: $input-height-sm=2.142rem, $font-size-sm=0.857rem, $line-height-sm=1 */
.vs-sm >>> .vs__dropdown-toggle {
  height: 2.142rem;
  min-height: 2.142rem;
  padding: 0 6px;
  border-radius: 0.357rem;
}
.vs-sm >>> .vs__search,
.vs-sm >>> .vs__search::placeholder,
.vs-sm >>> .vs__selected {
  font-size: 0.857rem;
  line-height: 1;
  margin: 0;
  padding: 0;
}
.vs-sm >>> .vs__actions {
  padding: 0 4px 0 2px;
}
</style>
